import { useEffect, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import { praxisAnzeige } from '../lib/praxis.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  GESPRAECH_ERGEBNIS_OPTIONS,
  STATUS_OPTIONS,
  TYP_OPTIONS,
  heuteAlsInputValue,
  toDateInputValue,
  userAnzeige,
} from '../lib/aktivitaet.js';
import { formatKontaktName } from '../lib/kontakt.js';

const EMPTY = {
  typ: 'aufgabe',
  titel: '',
  beschreibung: '',
  status: 'offen',
  faellig_am: '',
  uhrzeit: '',
  kontakt: '',
  zustaendig: '',
  gespraech_ergebnis: '',
};

// "Wiedervorlage zu: …" nicht stapeln, falls der alte Titel schon mit dem
// Präfix beginnt. Vergleich case-insensitive und Leerzeichen-tolerant.
function titelMitWiedervorlagePraefix(alt) {
  const text = (alt ?? '').toString();
  if (/^\s*wiedervorlage zu:/i.test(text)) return text;
  return `Wiedervorlage zu: ${text}`;
}

export default function AktivitaetFormModal({
  open,
  onClose,
  onSaved,
  praxisId,
  aktivitaet,
  kontakte = [],
  defaultValues = {},
  erlaubePraxisAuswahl = false,
}) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  // Folgetermin-Block, sichtbar sobald status === 'erledigt'. Wird beim
  // Speichern in eine eigenständige offene Aktivität mit ist_wiedervorlage
  // verwandelt — wenn der User ein Datum eingibt. Typ/Titel/Beschreibung
  // bekommen Defaults aus der Ursprungsaktivität, sind aber frei änderbar
  // (typischer Fall: erledigte E-Mail → Folgetermin als Anruf).
  const [folgeWiedervorlageAm, setFolgeWiedervorlageAm] = useState('');
  const [folgeTyp, setFolgeTyp] = useState('');
  const [folgeTitel, setFolgeTitel] = useState('');
  const [folgeBeschreibung, setFolgeBeschreibung] = useState('');
  // Einmalig pro Modal-Session: wir setzen die Folge-Defaults beim ERSTEN
  // Wechsel auf "erledigt". Wenn der User die Felder anfasst und den Status
  // zwischendurch wieder auf "offen" stellt, bleibt der Inhalt erhalten —
  // erst beim Schließen des Modals wird alles geleert.
  const [folgeFeldernInitialisiert, setFolgeFeldernInitialisiert] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Lokaler Modus: kein praxisId von außen → User wählt im Modal.
  // Wird auch genutzt, wenn die Praxis nachträglich gewechselt werden soll.
  const [praxenListe, setPraxenListe] = useState([]);
  const [gewaehltePraxis, setGewaehltePraxis] = useState('');
  const [praxisKontakte, setPraxisKontakte] = useState([]);

  // Effektive Praxis-ID = entweder Prop (Standardweg) oder lokale Auswahl.
  const effektivePraxisId = praxisId || gewaehltePraxis;
  const effektiveKontakte = praxisId ? kontakte : praxisKontakte;

  // Praxen-Liste laden, wenn der User die Praxis im Modal auswählen darf.
  useEffect(() => {
    if (!open || !erlaubePraxisAuswahl || praxisId) return;
    let cancelled = false;
    pb.collection('praxen')
      .getFullList({ sort: 'name', fields: 'id,name,name_zusatz' })
      .then((list) => {
        if (!cancelled) setPraxenListe(list);
      })
      .catch(() => {
        if (!cancelled) setPraxenListe([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, erlaubePraxisAuswahl, praxisId]);

  // Kontakte für die im Modal gewählte Praxis nachladen — nur im Picker-Modus.
  useEffect(() => {
    if (!open || praxisId || !gewaehltePraxis) {
      setPraxisKontakte([]);
      return undefined;
    }
    let cancelled = false;
    pb.collection('kontakte')
      .getFullList({
        filter: `praxis = "${gewaehltePraxis}"`,
        sort: 'nachname,vorname',
      })
      .then((list) => {
        if (!cancelled) setPraxisKontakte(list);
      })
      .catch(() => {
        if (!cancelled) setPraxisKontakte([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, praxisId, gewaehltePraxis]);

  // Auswahl beim erneuten Öffnen des Modals zurücksetzen.
  useEffect(() => {
    if (!open) {
      setGewaehltePraxis('');
      setPraxisKontakte([]);
    }
  }, [open]);

  // Users für Zuständig-Dropdown laden, sobald das Modal geöffnet wird.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    pb.collection('users')
      .getFullList({ sort: 'nachname,vorname,email' })
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setUsersError(
            err?.message ||
              'Mitarbeiter konnten nicht geladen werden — eventuell sind die users-Collection-Rules zu restriktiv.'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (aktivitaet) {
      setForm({
        typ: aktivitaet.typ ?? 'aufgabe',
        titel: aktivitaet.titel ?? '',
        beschreibung: aktivitaet.beschreibung ?? '',
        status: aktivitaet.status ?? 'offen',
        faellig_am: toDateInputValue(aktivitaet.faellig_am),
        uhrzeit: aktivitaet.uhrzeit ?? '',
        kontakt: aktivitaet.kontakt ?? '',
        zustaendig: aktivitaet.zustaendig ?? '',
        gespraech_ergebnis: aktivitaet.gespraech_ergebnis ?? '',
      });
    } else {
      setForm({
        ...EMPTY,
        // Default-Fälligkeit ist heute — kann durch defaultValues überschrieben
        // werden, wenn z. B. eine Aktivität dupliziert wird.
        faellig_am: heuteAlsInputValue(),
        zustaendig: user?.id ?? '',
        ...defaultValues,
      });
    }
    setFolgeWiedervorlageAm('');
    setFolgeTyp('');
    setFolgeTitel('');
    setFolgeBeschreibung('');
    setFolgeFeldernInitialisiert(false);
    setError(null);
  }, [open, aktivitaet, user, defaultValues]);

  // Defaults für den Folgetermin-Block beim ersten Wechsel auf "erledigt"
  // setzen. Bei nachträglichen Status-Wechseln (z. B. zurück auf "offen" und
  // wieder auf "erledigt") werden bereits eingetippte Werte NICHT
  // überschrieben — kontrolliert über das folgeFeldernInitialisiert-Flag.
  useEffect(() => {
    if (!open) return;
    if (form.status !== 'erledigt') return;
    if (folgeFeldernInitialisiert) return;
    setFolgeTyp(form.typ);
    setFolgeTitel(titelMitWiedervorlagePraefix(form.titel));
    setFolgeBeschreibung(form.beschreibung || '');
    setFolgeFeldernInitialisiert(true);
  }, [open, form.status, form.typ, form.titel, form.beschreibung, folgeFeldernInitialisiert]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!effektivePraxisId) {
      setError('Bitte eine Praxis auswählen.');
      return;
    }
    if (!form.faellig_am) {
      setError('Bitte ein Fälligkeitsdatum angeben.');
      return;
    }
    if (form.typ === 'telefonat' && form.status === 'erledigt' && !form.gespraech_ergebnis) {
      setError('Bitte ein Gesprächsergebnis auswählen.');
      return;
    }
    // Folgetermin nur dann gültig, wenn — sofern ein Datum gesetzt ist —
    // auch ein Titel da ist. Datum leer = kein Folgetermin, dann sind die
    // restlichen Folge-Felder schlicht egal.
    if (form.status === 'erledigt' && folgeWiedervorlageAm && !folgeTitel.trim()) {
      setError('Bitte einen Titel für die Folge-Aktivität angeben.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        praxis: effektivePraxisId,
        kontakt: form.kontakt || null,
        typ: form.typ,
        titel: form.titel,
        beschreibung: form.beschreibung,
        status: form.status,
        faellig_am: form.faellig_am || '',
        uhrzeit: form.uhrzeit || '',
        zustaendig: form.zustaendig || null,
        // Gesprächsergebnis nur bei erledigten Telefonaten persistieren — bei
        // Typ-Wechsel oder Status-Wechsel weg von "erledigt" wird der Wert
        // bewusst geleert, damit keine veralteten Ergebnisse hängenbleiben.
        gespraech_ergebnis:
          form.typ === 'telefonat' && form.status === 'erledigt'
            ? form.gespraech_ergebnis
            : '',
      };

      let saved;
      if (aktivitaet?.id) {
        saved = await pb.collection('aktivitaeten').update(aktivitaet.id, payload);
      } else {
        payload.erstellt_von = user?.id ?? null;
        saved = await pb.collection('aktivitaeten').create(payload);
      }

      // Folgetermin: wenn der User die Aktivität gerade erledigt und ein
      // Wiedervorlage-Datum eingegeben hat, parallel eine neue offene
      // Aktivität als Wiedervorlage anlegen. Schlägt das fehl, bleibt die
      // Erledigung der Ursprungsaktivität bestehen — der User bekommt nur
      // eine Fehlermeldung, sodass er die Wiedervorlage manuell nachholen
      // kann (kein implizites Rollback der Hauptspeicherung).
      const folgePayload =
        form.status === 'erledigt' && folgeWiedervorlageAm
          ? {
              praxis: effektivePraxisId,
              kontakt: form.kontakt || null,
              typ: folgeTyp,
              titel: folgeTitel,
              beschreibung: folgeBeschreibung || '',
              status: 'offen',
              faellig_am: folgeWiedervorlageAm,
              // Uhrzeit bewusst leer lassen — die alte Uhrzeit gehört zum
              // erledigten Termin und wäre auf der Wiedervorlage irreführend.
              uhrzeit: '',
              zustaendig: form.zustaendig || null,
              ist_wiedervorlage: true,
              vorgaenger_aktivitaet: saved?.id ?? null,
              erstellt_von: user?.id ?? null,
            }
          : null;

      if (folgePayload) {
        try {
          await pb.collection('aktivitaeten').create(folgePayload);
        } catch (folgeErr) {
          // finally() unten setzt saving auf false — kein doppelter Aufruf nötig.
          setError(
            (folgeErr?.message ||
              'Folgetermin konnte nicht angelegt werden.') +
              ' Die Erledigung der Ursprungsaktivität wurde gespeichert; ' +
              'bitte den Folgetermin manuell nachtragen.'
          );
          onSaved?.(saved);
          return;
        }
      }

      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={aktivitaet?.id ? 'Aktivität bearbeiten' : 'Neue Aktivität'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!praxisId && erlaubePraxisAuswahl && (
            <div className="sm:col-span-2">
              <label className="label">Praxis *</label>
              <select
                required
                className="input mt-1"
                value={gewaehltePraxis}
                onChange={(e) => setGewaehltePraxis(e.target.value)}
                disabled={!!aktivitaet?.id}
              >
                <option value="">— Praxis auswählen —</option>
                {praxenListe.map((p) => (
                  <option key={p.id} value={p.id}>
                    {praxisAnzeige(p)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="label">Typ *</label>
            <select required className="input mt-1" value={form.typ} onChange={handleChange('typ')}>
              {TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.icon} {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Status *</label>
            <select
              required
              className="input mt-1"
              value={form.status}
              onChange={handleChange('status')}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {form.status === 'erledigt' && (
            <div className="sm:col-span-2 space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="text-sm font-medium text-amber-900">
                Folgetermin / Wiedervorlage anlegen?
              </div>

              <div>
                <label className="label">Wiedervorlage am</label>
                <input
                  type="date"
                  className="input mt-1"
                  value={folgeWiedervorlageAm}
                  onChange={(e) => setFolgeWiedervorlageAm(e.target.value)}
                />
              </div>

              <div>
                <label className="label">Typ der Folge-Aktivität</label>
                <select
                  className="input mt-1"
                  value={folgeTyp}
                  onChange={(e) => setFolgeTyp(e.target.value)}
                >
                  {TYP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.icon} {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Titel der Folge-Aktivität</label>
                <input
                  type="text"
                  className="input mt-1"
                  value={folgeTitel}
                  onChange={(e) => setFolgeTitel(e.target.value)}
                  placeholder="z.B. Anruf wegen Mail vom 11.05."
                />
              </div>

              <div>
                <label className="label">Beschreibung der Folge-Aktivität</label>
                <textarea
                  rows={3}
                  className="input mt-1"
                  value={folgeBeschreibung}
                  onChange={(e) => setFolgeBeschreibung(e.target.value)}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Vorausgefüllt mit der Beschreibung der alten Aktivität — frei änderbar.
                </p>
              </div>

              <p className="text-xs text-slate-600">
                Wenn ein Datum gesetzt ist, wird beim Speichern automatisch eine
                neue offene Aktivität als Wiedervorlage angelegt. Typ, Titel und
                Beschreibung kannst du frei anpassen — z.B. eine erledigte E-Mail
                kann als nächster Schritt einen Anruf auslösen.
              </p>
            </div>
          )}

          {form.typ === 'telefonat' && form.status === 'erledigt' && (
            <div className="sm:col-span-2">
              <label className="label">Gesprächsergebnis *</label>
              <select
                required
                className="input mt-1"
                value={form.gespraech_ergebnis}
                onChange={handleChange('gespraech_ergebnis')}
              >
                <option value="">— bitte auswählen —</option>
                {GESPRAECH_ERGEBNIS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="sm:col-span-2">
            <label className="label">Titel *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.titel}
              onChange={handleChange('titel')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Beschreibung</label>
            <textarea
              rows={4}
              className="input mt-1"
              value={form.beschreibung}
              onChange={handleChange('beschreibung')}
            />
          </div>

          <div>
            <label className="label">Fällig am *</label>
            <div className="mt-1 flex gap-2">
              <input
                type="date"
                required
                className="input flex-1"
                value={form.faellig_am}
                onChange={handleChange('faellig_am')}
              />
              <input
                type="time"
                className="input w-28"
                value={form.uhrzeit}
                onChange={handleChange('uhrzeit')}
                placeholder="HH:MM"
                title="Uhrzeit (optional)"
              />
            </div>
          </div>

          <div>
            <label className="label">Kontakt (optional)</label>
            <select
              className="input mt-1"
              value={form.kontakt}
              onChange={handleChange('kontakt')}
            >
              <option value="">— keiner —</option>
              {effektiveKontakte.map((k) => (
                <option key={k.id} value={k.id}>
                  {formatKontaktName(k)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Verknüpft die Aktivität mit einem konkreten Ansprechpartner — z. B.
              wenn du gerade telefoniert oder gemailt hast.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Zuständig</label>
            <select
              className="input mt-1"
              value={form.zustaendig}
              onChange={handleChange('zustaendig')}
            >
              <option value="">— niemand —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {userAnzeige(u)}
                </option>
              ))}
            </select>
            {usersError && <p className="mt-1 text-xs text-amber-700">{usersError}</p>}
          </div>

        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
